import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import {
  Column,
  CustomAction,
  DataTableComponent,
  EmptyTableComponent,
} from '@nimmsta/ng-commons-lib/data-table';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { SoftwarePlatform } from '@nimmsta/ng-commons-lib/models';
import { SoftwarePlatformFormComponent } from './software-platform-form.component';
import { SoftwareLabelDatasource } from './software-label.datasource';
import { SoftwarePlatformService } from '../../services/software/software-platform.service';

@Component({
  selector: 'app-software-platform',
  templateUrl: './software-platform.component.html',
  imports: [
    SoftwarePlatformFormComponent,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    AppModelModalComponent,
    HeaderBarComponent,
  ],
})
export class SoftwarePlatformComponent implements OnInit {
  private softwarePlatformService = inject(SoftwarePlatformService);
  private translate = inject(TranslateService);
  dataSource!: SoftwareLabelDatasource<SoftwarePlatform>;

  @ViewChild(DataTableComponent, { static: true })
  private tableComponent!: DataTableComponent<SoftwarePlatform>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<SoftwarePlatform>;

  @ViewChild(SoftwarePlatformFormComponent, { static: true })
  private formComponent!: SoftwarePlatformFormComponent;

  customActions: CustomAction<SoftwarePlatform>[] = [];

  get columns(): Column[] {
    return [
      new Column('name', 'software.label.name', { sortable: true }),
      new Column('description', 'software.label.description'),
    ];
  }

  ngOnInit(): void {
    this.dataSource = new SoftwareLabelDatasource<SoftwarePlatform>(
      this.softwarePlatformService,
    );
    const editAction = new CustomAction('edit');
    this.customActions.push(editAction);
    editAction.subscribe((id) => this.edit(id));
    const deleteAction = new CustomAction('delete', false, true);
    this.customActions.push(deleteAction);
    deleteAction.subscribe((id) => this.delete(id));
  }

  new(): void {
    this.translate.get('new').subscribe((value) => {
      this.openModal(value, true, new SoftwarePlatform());
    });
  }

  show(id: string): void {
    this.translate.get('details').subscribe((value) => {
      this.openModal(value, false, this.getSelectedObject(id)!);
    });
  }

  edit(id: string): void {
    this.translate.get('edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(
    title: string,
    editable: boolean,
    platform: SoftwarePlatform,
  ): void {
    this.formComponent.editable = editable;
    this.formComponent.setValue(platform);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  delete(id: string): void {
    lastValueFrom(
      this.softwarePlatformService.delete(this.getSelectedObject(id)!),
    ).then(() => this.tableComponent.loadDataPage());
  }

  onSave(): void {
    this.formComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.tableComponent.loadDataPage();
  }

  getSelectedObject(id: string): SoftwarePlatform | undefined {
    return this.dataSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
